// H:\PricingPenguin\PricingPenguinProj\pricing_penguin_frontend\src\components\common\AppRoutes.jsx

import React, { Suspense } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import lazy from "../../utils/lazyWithReload";
import PersistLogin from "./PersistLogin";
import AuthMiddleware from "../../middleware/AuthMiddleware";
import GuestMiddleware from "../../middleware/GuestMiddleware";
import useAuth from "../../hooks/useAuth";
import SplashScreen from "./SplashScreen";

// ============================================================================
// CORE PAGES - Eagerly loaded (critical path for initial render)
// ============================================================================
import Home from "../../pages/Home";
import NotFound from "../../pages/NotFound";

// Lazy on purpose: AdminMiddleware drags AdminSidebar's ~26 react-icons into
// whatever bundle it lands in, and Login pulls the Google/Apple auth SDKs —
// neither belongs in the public visitor's critical path. A lazy layout route
// suspends exactly like a lazy page under the app-wide <Suspense>.
const AdminMiddleware = lazy(() => import("../../middleware/AdminMiddleware"));
const Login = lazy(() => import("../../pages/Login"));

// ============================================================================
// LAZY-LOADED PAGES - Code-split for smaller initial bundle
// ============================================================================

// Auth & Onboarding
const Signup = lazy(() => import("../../pages/Signup"));
const SignupContractor = lazy(() => import("../../pages/SignupContractor"));
const SignupHomeowner = lazy(() => import("../../pages/SignupHomeowner"));
const ForgotPassword = lazy(() => import("../../pages/ForgotPassword"));
const ResetPassword = lazy(() => import("../../pages/ResetPassword"));
const VerifySuccess = lazy(() => import("../../pages/VerifySuccess"));
const VerifyFailure = lazy(() => import("../../pages/VerifyFailure"));
const EmailVerificationHandler = lazy(() => import("../../handlers/EmailVerificationHandler"));
const SecureAccount = lazy(() => import("../../pages/SecureAccount"));
const SecureAccountSuccess = lazy(() => import("../../pages/SecureAccountSuccess"));
const CompleteProfile = lazy(() => import("../../pages/CompleteProfile"));
const ContractorPasswordSetup = lazy(() => import("../../pages/ContractorPasswordSetup"));
const ClaimProfile = lazy(() => import("../../pages/ClaimProfile"));
const ContractorQuiz = lazy(() => import("../../pages/ContractorQuiz"));
const Unsubscribe = lazy(() => import("../../pages/Unsubscribe"));

// Public Pages
const ContractorSearch = lazy(() => import("../../pages/ContractorSearch"));
const ContractorPackages = lazy(() => import("../../pages/ContractorPackages"));
const CompanyPortfolio = lazy(() => import("../../pages/CompanyPortfolio"));
const Chatbox = lazy(() => import("../../pages/Chatbox"));
const WidgetChat = lazy(() => import("../../pages/WidgetChat"));
const ContractorWidget = lazy(() => import("../../pages/ContractorWidget"));
const Blog = lazy(() => import("../../pages/Blog"));
const BlogDetails = lazy(() => import("../../pages/BlogDetails"));
const AllReviews = lazy(() => import("../../pages/AllReviews"));
const ProjectSummaryPage = lazy(() => import("../../pages/ProjectSummaryPage"));
const FindContractorPage = lazy(() => import("../../pages/FindContractorPage"));
const FreeResources = lazy(() => import("../../pages/FreeResources"));
const LearnMore = lazy(() => import("../../pages/LearnMore"));
const NewsletterHomeowner = lazy(() => import("../../pages/NewsletterHomeowner"));
const NewsletterTrades = lazy(() => import("../../pages/NewsletterTrades"));
const NewsletterConfirm = lazy(() => import("../../pages/NewsletterConfirm"));
const DisableAdblock = lazy(() => import("../../pages/DisableAdblock"));
const WebsiteFeedback = lazy(() => import("../../pages/WebsiteFeedback"));
const ForTradespeople = lazy(() => import("../../pages/ForTradespeople"));
const CostGuide = lazy(() => import("../../pages/CostGuide"));
const CostGuidesIndex = lazy(() => import("../../pages/CostGuidesIndex"));
const TradeAdvice = lazy(() => import("../../pages/TradeAdvice"));
const TradeAdviceIndex = lazy(() => import("../../pages/TradeAdviceIndex"));
const Estimator = lazy(() => import("../../pages/Estimator"));
const EstimatorsIndex = lazy(() => import("../../pages/EstimatorsIndex"));
const CostLandingPage = lazy(() => import("../../pages/CostLandingPage"));
const TradesDayRates = lazy(() => import("../../pages/TradesDayRates"));
const CostReport = lazy(() => import("../../pages/CostReport"));
const PenguinIndex = lazy(() => import("../../pages/PenguinIndex"));
const LocationPage = lazy(() => import("../../pages/LocationPage"));
const WarmHomesPlan = lazy(() => import("../../pages/WarmHomesPlan"));
const CostCalculator = lazy(() => import("../../pages/CostCalculator"));
const WidgetInstallation = lazy(() => import("../../pages/WidgetInstallation"));
const QuoteCheck = lazy(() => import("../../pages/QuoteCheck"));
const TrustedByPercy = lazy(() => import("../../pages/TrustedByPercy"));
const TrustScorePage = lazy(() => import("../../pages/TrustScorePage"));
const HomeownerTrust = lazy(() => import("../../pages/HomeownerTrust"));
const VerifyBadge = lazy(() => import("../../pages/VerifyBadge"));
const HomeHealthPreview = lazy(() => import("../../pages/HomeHealthPreview"));
const Partners = lazy(() => import("../../pages/Partners"));
const AppsPage = lazy(() => import("../../pages/AppsPage"));

// Footer Pages
const About = lazy(() => import("../../pages/footer/About"));
const Careers = lazy(() => import("../../pages/footer/Careers"));
const Sustainability = lazy(() => import("../../pages/footer/Sustainability"));
const FAQs = lazy(() => import("../../pages/footer/FAQs"));
const Contact = lazy(() => import("../../pages/footer/Contact"));
const Support = lazy(() => import("../../pages/footer/Support"));
const Policies = lazy(() => import("../../pages/footer/Policies"));

// Payment Completion Pages
const SignupCompletion = lazy(() => import("../payment/SignupCompletion"));
const UpgradeCompletion = lazy(() => import("../payment/UpgradeCompletion"));
const CancelCompletion = lazy(() => import("../payment/CancelCompletion"));
const DowngradeCompletion = lazy(() => import("../payment/DowngradeCompletion"));
const AddPaymentLink = lazy(() => import("../payment/AddPaymentLink"));
const PaymentSaved = lazy(() => import("../payment/PaymentSaved"));

// Protected User Pages
const ContractorHome = lazy(() => import("../../pages/ContractorHome"));
const ContractorProfile = lazy(() => import("../../pages/ContractorProfile"));
const HomeownerProfile = lazy(() => import("../../pages/HomeownerProfile"));
const HomeHealthDashboard = lazy(() => import("../../pages/HomeHealthDashboard"));
const MessagesInbox = lazy(() => import("../../pages/MessagesInbox"));
const MessageThread = lazy(() => import("../../pages/MessageThread"));
const EmergencyConnect = lazy(() => import("../../pages/EmergencyConnect"));

// Admin Pages
const AdminLogin = lazy(() => import("../../pages/admin/AdminLogin"));
const AdminPasswordSetup = lazy(() => import("../../pages/admin/AdminPasswordSetup"));
const AdminDashboard = lazy(() => import("../../pages/admin/AdminDashboard"));
const MerchantDashboard = lazy(() => import("../../pages/admin/MerchantDashboard"));
const ManageContractors = lazy(() => import("../../pages/admin/ManageContractors"));
const EditContractor = lazy(() => import("../../pages/admin/EditContractor"));
const AddContractorWizard = lazy(() => import("../../pages/admin/AddContractorWizard"));
const ContractorPipeline = lazy(() => import("../../pages/admin/ContractorPipeline"));
const ManageHomeowners = lazy(() => import("../../pages/admin/ManageHomeowners"));
const EditHomeowner = lazy(() => import("../../pages/admin/EditHomeowner"));
const ReviewQueue = lazy(() => import("../../pages/admin/ReviewQueue"));
const PercyEvidenceQueue = lazy(() => import("../../pages/admin/PercyEvidenceQueue"));
const ManageBlogs = lazy(() => import("../../pages/admin/ManageBlogs"));
const EditBlog = lazy(() => import("../../pages/admin/EditBlog"));
const EngagementDashboard = lazy(() => import("../../pages/admin/EngagementDashboard"));
const SiteAnalytics = lazy(() => import("../../pages/admin/SiteAnalytics"));
const InviteAdmin = lazy(() => import("../../pages/admin/InviteAdmin"));
const ManageEmails = lazy(() => import("../../pages/admin/ManageEmails"));
const EditEmail = lazy(() => import("../../pages/admin/EditEmail"));
const ManageEmailDetail = lazy(() => import("../../pages/admin/ManageEmailDetail"));
const ManageSalesLeads = lazy(() => import("../../pages/admin/ManageSalesLeads"));
const MyCallList = lazy(() => import("../../pages/admin/MyCallList"));
const UntouchedProspectsPage = lazy(() => import("../../pages/admin/UntouchedProspectsPage"));
const ShadowListings = lazy(() => import("../../pages/admin/ShadowListings"));
const ShadowListingAdd = lazy(() => import("../../pages/admin/ShadowListingAdd"));
const ScraperManager = lazy(() => import("../../pages/admin/ScraperManager"));
const ScraperReview = lazy(() => import("../../pages/admin/ScraperReview"));
const EmailCampaignBuilder = lazy(() => import("../../pages/admin/EmailCampaignBuilder"));
const ManageEmailGroups = lazy(() => import("../../pages/admin/ManageEmailGroups"));
const ProspectCleanup = lazy(() => import("../../pages/admin/ProspectCleanup"));
const JobDataCMS = lazy(() => import("../../pages/admin/JobDataCMS"));
const AIEmbeddingsManager = lazy(() => import("../../pages/admin/AIEmbeddingsManager"));
const ChatLogs = lazy(() => import("../../pages/admin/ChatLogs"));
const AiTraining = lazy(() => import("../../pages/admin/AiTraining"));
const DatabaseTables = lazy(() => import("../../pages/admin/DatabaseTables"));
const ManagePromoCodes = lazy(() => import("../../pages/admin/ManagePromoCodes"));
const ManagePackages = lazy(() => import("../../pages/admin/ManagePackages"));
const ManageJobOpenings = lazy(() => import("../../pages/admin/ManageJobOpenings"));
const PublicImages = lazy(() => import("../../pages/admin/PublicImages"));
const LocationEmailTool = lazy(() => import("../../pages/admin/LocationEmailTool"));
const GenericPackageEmailTool = lazy(() => import("../../pages/admin/GenericPackageEmailTool"));
const ProspectsSummaryCard = lazy(() => import("../../pages/admin/ProspectsSummaryCard"));
const SalesPerformanceReport = lazy(() => import("../../pages/admin/SalesPerformanceReport"));
const MonthlyCommissionsReport = lazy(() => import("../../pages/admin/MonthlyCommissionsReport"));
const SalesCommissionsReport = lazy(() => import("../../pages/admin/SalesCommissionsReport"));
const SalesTeamActivity = lazy(() => import("../../pages/admin/SalesTeamActivity"));
const LeadsOverview = lazy(() => import("../../pages/admin/LeadsOverview"));
const MarketIntelligenceTool = lazy(() => import("../../pages/admin/MarketIntelligenceTool"));

// ============================================================================
// ROUTE COMPONENTS
// ============================================================================

const LazyFallback = () => <SplashScreen loadingMessage="Loading page..." />;

const FullAdminRoute = ({ children }) => {
 const { user } = useAuth();

 if (user?.user_type !== 'admin') {
  return <Navigate to="/admin/dashboard" replace />;
 }

 return children;
};


const AppRoutes = ({
  jobCategories,
  subscriptionPackages,
  reviews,
  isDataLoading,
  isReviewsLoading,
  reviewsError,
  retryReviews,
}) => {
  const { user, profileLoading, setUser } = useAuth();

  if (profileLoading) {
    return <SplashScreen loadingMessage="Loading your profile..." />;
  }

  const handleProfileUpdate = (updatedContractor) => {
    setUser((prevUser) => ({
      ...prevUser,
      contractor_profile: updatedContractor,
    }));
  };

  return (
    <Suspense fallback={<LazyFallback />}>
      <Routes>
        <Route path="/" element={<PersistLogin />}>
          <Route
            index
            element={
              user ? (
                user.user_type === "admin" || user.user_type === "sales" ? (
                  <Navigate to="/admin/dashboard" replace />
                ) : user.user_type === "merchant" || user.user_type === "partner" ? (
                  <Navigate to="/admin/merchant-dashboard" replace />
                ) : user.user_type === "contractor" ? (
                  <Navigate to="/contractor-home" replace />
                ) : (
                  <Navigate to="/home" replace />
                )
              ) : (
                <Home
                  reviews={reviews}
                  isDataLoading={isDataLoading}
                  isReviewsLoading={isReviewsLoading}
                  reviewsError={reviewsError}
                  retryReviews={retryReviews}
                />
              )
            }
          />

          <Route element={<GuestMiddleware />}>
            <Route
              path="home"
              element={
                <Home
                  reviews={reviews}
                  isDataLoading={isDataLoading}
                  isReviewsLoading={isReviewsLoading}
                  reviewsError={reviewsError}
                  retryReviews={retryReviews}
                />
              }
            />
            <Route path="login" element={<Login />} />
            <Route
              path="signup"
              element={
                <Signup
                  jobCategories={jobCategories}
                  subscriptionPackages={subscriptionPackages}
                />
              }
            />
            <Route path="contractor-packages" element={<ContractorPackages />} />
            <Route
              path="signup/contractor"
              element={
                <SignupContractor
                  jobCategories={jobCategories}
                  subscriptionPackages={subscriptionPackages}
                />
              }
            />
            <Route path="signup/homeowner" element={<SignupHomeowner />} />
            <Route
              path="local-contractors"
              element={
                <ContractorSearch
                  jobCategories={jobCategories}
                  subscriptionPackages={subscriptionPackages}
                />
              }
            />
            <Route
              path="contractor-search/:contractor_id"
              element={<CompanyPortfolio />}
            />
            <Route
              path="contractor/:slug"
              element={<CompanyPortfolio />}
            />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="reset-password" element={<ResetPassword />} />
            <Route path="signup-completion" element={<SignupCompletion />} />
            <Route path="upgrade-completion" element={<UpgradeCompletion />} />
            <Route path="cancel-completion" element={<CancelCompletion />} />
            <Route
              path="downgrade-completion"
              element={<DowngradeCompletion />}
            />
            <Route path="add-payment/:token" element={<AddPaymentLink />} />
            <Route path="payment-saved" element={<PaymentSaved />} />

            <Route path="contractor-quiz" element={<ContractorQuiz />} />

            {/* --- Verification Routes --- */}
            <Route
              path="/verify-email/:uidb64/:token"
              element={<EmailVerificationHandler />}
            />
            <Route
              path="/verify-success"
              element={<VerifySuccess />}
            />
            <Route
              path="/verify-failure"
              element={<VerifyFailure />}
            />

            {/* Email unsubscribe route */}
            <Route path="unsubscribe" element={<Unsubscribe />} />

            <Route path="secure-account" element={<SecureAccount />} />
            <Route
              path="secure-account-success"
              element={<SecureAccountSuccess />}
            />
            <Route path="blog" element={<Blog />} />
            <Route path="blog/:id" element={<BlogDetails />} />
            <Route path="reviews" element={<AllReviews reviews={reviews} />} />

            <Route path="chat" element={<Chatbox />} />
            <Route path="chat/:conversationId" element={<Chatbox />} />
            <Route path="project-summary" element={<ProjectSummaryPage />} />
            <Route path="find-contractor" element={<FindContractorPage />} />

            <Route path="free-resources" element={<FreeResources />} />
            <Route path="learn-more" element={<LearnMore />} />
            <Route path="newsletter" element={<NewsletterHomeowner />} />
            <Route path="trades-newsletter" element={<NewsletterTrades />} />
            <Route path="newsletter/confirm" element={<NewsletterConfirm />} />
            <Route path="quote-check" element={<QuoteCheck />} />
            <Route path="trusted-by-percy" element={<TrustedByPercy />} />
            <Route path="trust-score" element={<TrustScorePage />} />
            <Route path="homeowner-trust" element={<HomeownerTrust />} />
            <Route path="verify" element={<VerifyBadge />} />
            <Route path="verify/:percyNumber" element={<VerifyBadge />} />
            <Route path="your-home" element={<HomeHealthPreview />} />
            <Route path="apps" element={<AppsPage />} />
            <Route path="about" element={<About />} />
            <Route path="careers" element={<Careers />} />
            <Route path="sustainability" element={<Sustainability />} />
            <Route path="faqs" element={<FAQs />} />
            <Route path="contact" element={<Contact />} />
            <Route path="support" element={<Support />} />
            <Route path="policies" element={<Policies />} />
            <Route path="disable-adblock" element={<DisableAdblock />} />
            <Route path="for-tradespeople" element={<ForTradespeople />} />
            <Route path="cost-guides" element={<CostGuidesIndex />} />
            <Route path="cost-guides/:slug" element={<CostGuide />} />
            <Route path="trade-advice" element={<TradeAdviceIndex />} />
            <Route path="trade-advice/:slug" element={<TradeAdvice />} />
            <Route path="estimators" element={<EstimatorsIndex />} />
            <Route path="estimators/:slug" element={<Estimator />} />
            <Route path="costs/:slug" element={<CostLandingPage />} />
            <Route path="day-rates" element={<TradesDayRates />} />
            <Route path="tradespeople/:trade/:city" element={<LocationPage />} />
            <Route path="warm-homes-plan" element={<WarmHomesPlan />} />
            <Route path="cost-calculator" element={<CostCalculator />} />
            <Route path="widget-installation" element={<WidgetInstallation />} />
            <Route path="partners" element={<Partners />} />
            <Route path="reports/uk-home-improvement-costs-2026" element={<CostReport />} />
            <Route path="reports/penguin-index" element={<PenguinIndex />} />

            {/* --- Protected Routes (Require Access Token) --- */}
            <Route element={<AuthMiddleware />}>
              <Route path="website-feedback" element={<WebsiteFeedback />} />
              <Route
                path="contractor-home"
                element={<ContractorHome subscriptionPackages={subscriptionPackages} />}
              />
              <Route path="homeowner-profile" element={<HomeownerProfile />} />
              <Route path="home-health" element={<HomeHealthDashboard />} />
              <Route
                path="contractor-profile"
                element={
                  <ContractorProfile
                    jobCategories={jobCategories}
                    subscriptionPackages={subscriptionPackages}
                    onUpdate={handleProfileUpdate}
                  />
                }
              />
              <Route path="/complete-profile" element={<CompleteProfile />} />
              <Route path="messages" element={<MessagesInbox />} />
              <Route path="messages/:threadId" element={<MessageThread />} />
              <Route path="emergency" element={<EmergencyConnect />} />
              <Route path="contractor-widget" element={<ContractorWidget />} />
            </Route> {/* End AuthMiddleware */}

          </Route> {/* End GuestMiddleware */}

          {/* Embedded partner widget: public, framed by third-party sites,
              so it sits outside the guest redirect guard. */}
          <Route path="widget/:widgetId" element={<WidgetChat />} />

          {/* --- Admin Routes (Separate Auth) --- */}
          <Route path="/penguin-admin-access" element={<AdminLogin />} />
          <Route path="/admin-password-setup" element={<AdminPasswordSetup />} />
          <Route path="/contractor-password-setup" element={<ContractorPasswordSetup />} />
          <Route path="/claim-profile" element={<ClaimProfile />} />

          <Route path="/admin" element={<AdminMiddleware />}>
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="merchant-dashboard" element={<MerchantDashboard />} />
            <Route path="add-contractor" element={<AddContractorWizard />} />
            <Route path="manage-contractors" element={<ManageContractors />} />
            <Route path="manage-contractors/:id" element={<EditContractor />} />
            <Route path="contractors/pipeline" element={<ContractorPipeline />} />
            <Route path="shadow-listings" element={<ShadowListings />} />
            <Route path="shadow-listings/add" element={<ShadowListingAdd />} />
            <Route path="scraper" element={<FullAdminRoute><ScraperManager /></FullAdminRoute>} />
            <Route path="scraper/review" element={<FullAdminRoute><ScraperReview /></FullAdminRoute>} />
            <Route path="engagement" element={<EngagementDashboard />} />
            <Route path="site-analytics" element={<FullAdminRoute><SiteAnalytics /></FullAdminRoute>} />
            <Route path="untouched-prospects" element={<UntouchedProspectsPage />} />
            <Route path="my-call-list" element={<MyCallList />} />
            <Route path="sales-team-activity" element={<FullAdminRoute><SalesTeamActivity /></FullAdminRoute>} />
            <Route path="send-payment-link" element={<Navigate to="/admin/add-contractor" replace />} />

            {/* Full Admin Only Routes */}
            <Route path="manage-sales-leads" element={<FullAdminRoute><ManageSalesLeads /></FullAdminRoute>} />
            <Route path="manage-homeowners" element={<FullAdminRoute><ManageHomeowners /></FullAdminRoute>} />
            <Route path="manage-homeowners/:id" element={<FullAdminRoute><EditHomeowner /></FullAdminRoute>} />
            <Route path="review-queue" element={<FullAdminRoute><ReviewQueue /></FullAdminRoute>} />
            <Route path="percy-evidence" element={<FullAdminRoute><PercyEvidenceQueue /></FullAdminRoute>} />
            <Route path="manage-blogs" element={<FullAdminRoute><ManageBlogs /></FullAdminRoute>} />
            <Route path="manage-blogs/new" element={<FullAdminRoute><EditBlog /></FullAdminRoute>} />
            <Route path="manage-blogs/:slug" element={<FullAdminRoute><EditBlog /></FullAdminRoute>} />
            <Route path="invite-admin" element={<FullAdminRoute><InviteAdmin /></FullAdminRoute>} />

            {/* Email Management */}
            <Route path="manage-emails" element={<FullAdminRoute><ManageEmails /></FullAdminRoute>} />
            <Route path="manage-emails/:filename" element={<FullAdminRoute><ManageEmailDetail /></FullAdminRoute>} />
            <Route path="manage-emails/:filename/edit" element={<FullAdminRoute><EditEmail /></FullAdminRoute>} />
            <Route path="public-images" element={<FullAdminRoute><PublicImages /></FullAdminRoute>} />

            {/* Job Data Management (Combined Editor + Database + Sync & Reindex) */}
            <Route path="job-data" element={<FullAdminRoute><JobDataCMS /></FullAdminRoute>} />

            {/* Database Table Management (also accessible standalone) */}
            <Route path="database-tables" element={<FullAdminRoute><DatabaseTables /></FullAdminRoute>} />

            {/* Email Campaign Builder */}
            <Route path="campaigns" element={<FullAdminRoute><EmailCampaignBuilder /></FullAdminRoute>} />
            <Route path="campaigns/cleanup" element={<FullAdminRoute><ProspectCleanup /></FullAdminRoute>} />
            <Route path="email-groups" element={<FullAdminRoute><ManageEmailGroups /></FullAdminRoute>} />

            {/* AI Embeddings Manager */}
            <Route path="embeddings" element={<FullAdminRoute><AIEmbeddingsManager /></FullAdminRoute>} />

            {/* AI Chat Logs — conversations and uploaded images */}
            <Route path="chat-logs" element={<FullAdminRoute><ChatLogs /></FullAdminRoute>} />

            {/* AI Training — quality metrics, feedback, fine-tuning */}
            <Route path="ai-training" element={<FullAdminRoute><AiTraining /></FullAdminRoute>} />

            {/* Promotion Codes & Affiliate Management */}
            <Route path="promo-codes" element={<FullAdminRoute><ManagePromoCodes /></FullAdminRoute>} />
            <Route path="manage-packages" element={<FullAdminRoute><ManagePackages /></FullAdminRoute>} />
            <Route path="job-openings" element={<FullAdminRoute><ManageJobOpenings /></FullAdminRoute>} />

            {/* Admin Tools (extracted from dashboard) */}
            <Route path="tools/email-lookup" element={<LocationEmailTool />} />
            <Route path="tools/send-packages" element={<FullAdminRoute><GenericPackageEmailTool /></FullAdminRoute>} />
            <Route path="tools/sales-report" element={<FullAdminRoute><SalesPerformanceReport /></FullAdminRoute>} />
            <Route path="tools/commissions" element={<FullAdminRoute><MonthlyCommissionsReport /></FullAdminRoute>} />
            {/* Sales commission report is accessible to sales too — they see only their own rows */}
            <Route path="tools/sales-commissions" element={<SalesCommissionsReport />} />
            <Route path="tools/leads-overview" element={<FullAdminRoute><LeadsOverview /></FullAdminRoute>} />
            <Route path="tools/market-intelligence" element={<FullAdminRoute><MarketIntelligenceTool /></FullAdminRoute>} />
            <Route path="prospects-summary" element={<ProspectsSummaryCard />} />
          </Route> {/* End AdminMiddleware */}

          {/* --- Catch All: render a real 404 (noindex) instead of silently
               redirecting unknown URLs to the homepage, which created soft 404s. --- */}
          <Route path="*" element={<NotFound />} />
        </Route> {/* End PersistLogin */}
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
